/**
 * Smooth progress animator that gradually increases progress to target
 */
export class SmoothProgressAnimator {
  private currentProgress: number = 0;
  private targetProgress: number = 0;
  private intervalId: NodeJS.Timeout | null = null;
  private onProgressUpdate: (progress: number) => void;
  private readonly updateInterval: number = 50; // Update every 50ms
  private readonly incrementPerUpdate: number = 1; // Increase by 1% per update

  constructor(onProgressUpdate: (progress: number) => void) {
    this.onProgressUpdate = onProgressUpdate;
  }

  /**
   * Set target progress and start animating towards it
   */
  setTarget(target: number): void {
    this.targetProgress = Math.min(target, 100);
    // If animation is not running, start it
    if (this.intervalId === null) {
      this.startAnimation();
    }
  }

  /**
   * Start the animation loop
   */
  private startAnimation(): void {
    if (this.intervalId !== null) {
      return; // Already running
    }
    this.intervalId = setInterval(() => {
      if (this.currentProgress < this.targetProgress) {
        this.currentProgress = Math.min(this.currentProgress + this.incrementPerUpdate, this.targetProgress);
        this.onProgressUpdate(Math.round(this.currentProgress));
      }
      // Reached target, but don't stop - wait for new target
      // Only stop if we're at 100% and target is 100%
      if (this.currentProgress >= 100 && this.targetProgress >= 100) {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      }
    }, this.updateInterval);
  }

  /**
   * Immediately set progress without animation (for final completion)
   */
  setImmediate(progress: number): void {
    this.currentProgress = progress;
    this.targetProgress = progress;
    this.onProgressUpdate(Math.round(progress));
  }

  /**
   * Clean up the animator
   */
  cleanup(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
